<template lang="pug">
section.lfc-section(ref="el" :id="sectionId")
  h2.lfc-section-heading
    span.lfc-num(v-if="numbered && myIndex !== -1" aria-hidden="true") {{ myIndex + 1 }}.
    | {{ __(heading) }}
  .lfc-section-body
    slot
</template>

<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, ref, type Ref } from 'vue'

interface SectionMeta {
  id: string
  heading: string
}

defineProps<{ heading: string }>()

const el = ref<HTMLElement | null>(null)

const numbered = inject<Ref<boolean>>('lfc:numbered', ref(false))
const registeredSections = inject<Ref<SectionMeta[]> | null>('lfc:registeredSections', null)
const claimSectionId = inject<(() => string | null) | null>('lfc:claimId', null)
const attachEl = inject<((id: string, el: HTMLElement) => void) | null>('lfc:attachEl', null)
const detachEl = inject<((id: string) => void) | null>('lfc:detachEl', null)

// Claimed synchronously in setup() (not onMounted) so this section's id is
// resolved during SSR too, in lockstep with LongFormContent's own
// registeredSections - see the comment there for why onMounted was too late.
const sectionId = claimSectionId ? claimSectionId() : null

const myIndex = computed(() => {
  if (!sectionId || !registeredSections) return -1

  return registeredSections.value.findIndex((s) => s.id === sectionId)
})

onMounted(() => {
  if (!el.value || !sectionId) return
  attachEl?.(sectionId, el.value)
})

onUnmounted(() => {
  if (sectionId) detachEl?.(sectionId)
})
</script>

<style lang="scss" scoped>
.lfc-section {
  scroll-margin-top: var(--lfc-offset, 1rem);
  margin-bottom: $space-xxl;

  &:last-child {
    margin-bottom: 0;
  }
}

.lfc-section-heading {
  margin-bottom: $space-m;
}

.lfc-num {
  font-variant-numeric: tabular-nums;
  margin-right: $space-xxs;
}

.lfc-section-body {
  :deep(p) {
    margin-bottom: $space-s;
  }

  :deep(a) {
    text-decoration: underline;
  }
}
</style>
